import React, { createContext, useContext, useState, useEffect } from 'react';
import { notifications } from '@mantine/notifications';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on app load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token with backend
      fetchUserProfile(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async (token) => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        notifications.show({
          title: 'Welcome back!',
          message: `Hello ${data.user.firstName || data.user.username}`,
          color: 'green',
        });
        return { success: true };
      } else {
        notifications.show({
          title: 'Login failed',
          message: data.message || 'Invalid credentials',
          color: 'red',
        });
        return { success: false, error: data.message };
      }
    } catch (error) {
      notifications.show({
        title: 'Login failed',
        message: 'Network error. Please try again.',
        color: 'red',
      });
      return { success: false, error: 'Network error' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        notifications.show({
          title: 'Welcome!',
          message: `Account created successfully, ${data.user.firstName || data.user.username}`,
          color: 'green',
        });
        return { success: true };
      } else {
        notifications.show({
          title: 'Registration failed',
          message: data.message || 'Failed to create account',
          color: 'red',
        });
        return { success: false, error: data.message };
      }
    } catch (error) {
      notifications.show({
        title: 'Registration failed',
        message: 'Network error. Please try again.',
        color: 'red',
      });
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    notifications.show({
      title: 'Logged out',
      message: 'You have been successfully logged out',
      color: 'blue',
    });
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
